import GridViewIcon from "@mui/icons-material/GridView"
import ListAltIcon from "@mui/icons-material/ListAlt"
import SearchIcon from "@mui/icons-material/Search"
import TocIcon from "@mui/icons-material/Toc"
import {
	Button,
	Divider,
	Grid,
	IconButton,
	InputBase,
	Paper,
	Tooltip,
} from "@mui/material"
import { styled, useTheme } from "@mui/material/styles"
import { format } from "date-fns"
import {
	ChangeEvent,
	FormEvent,
	FunctionComponent,
	useCallback,
	useEffect,
	useState,
} from "react"
import "react-date-range/dist/styles.css" // main css file
import "react-date-range/dist/theme/default.css" // theme css file
import { useTranslation } from "react-i18next"
import { useInView } from "react-intersection-observer"
import { useDispatch, useSelector } from "react-redux"
import { useLocation, useNavigate, useParams } from "react-router"

import Breadcrumb from "../../components/breadcrumb"
import SpinnerCard from "../../components/file/spinner/card"
import FiltersPlick from "../../components/filters/plick"
import PlickListComponent from "../../components/plick/list"
import EnhancedPlickTable from "../../components/plick/table"
import Sort from "../../components/sort"
import { AppDispatch } from "../../store"
import { BankEssential } from "../../store/actions/bank"
import { CampaignsPaginatedListState } from "../../store/actions/campaigns"
import { fetchFileDetail, getFileDetail } from "../../store/actions/file-detail"
import { resetDefault } from "../../store/files-list/files-slice"
import { RootState } from "../../store/reducers"

export interface IPlicksProperties {}

export interface IDatePicker {
	startDate: undefined | Date
	endDate: undefined | Date
	key: string
}

const PlicksList: FunctionComponent<IPlicksProperties> = () => {
	const navigate = useNavigate()
	const theme = useTheme()
	const { t } = useTranslation()
	const location = useLocation()
	const dispatch = useDispatch<AppDispatch>()
	const { fileId } = useParams()
	const searchParams = new URLSearchParams(window.location.search)
	const fileName = searchParams.get("filename")
	const authState = useSelector((state: RootState) => state.auth)
	const bankSelectedState: BankEssential | undefined = useSelector(
		(state: RootState) => state.banksUser.selected
	)
	const [filterBank, setFilterBank] = useState<undefined | string>(
		authState.user?.idBank ?? bankSelectedState?.id
	)
	const campaignSelectedState: CampaignsPaginatedListState | undefined =
		useSelector((state: RootState) => state.campaignsUser?.selected)

	const corporateSelectedState = useSelector(
		(state: RootState) => state.corporatesPaginated.selected
	)
	const PlickLoadingState = useSelector(
		(state: RootState) => state.plicks.loading
	)
	const plickFetchLoadingState = useSelector(
		(state: RootState) => state.plicks.loadingFetch
	)
	const plicksnextUrlState = useSelector(
		(state: RootState) => state.plicks.list.nextUrl
	)
	const permissions = useSelector(
		(state: RootState) => state.auth?.user?.permissions
	)

	const hasViewTransactionEnabled: boolean = permissions
		? permissions.some(
				(perm) =>
					perm.permissionName === "VIEW_TRANSACTION" && perm.enabled === true
			)
		: false

	const hasCrudFileEnabled: boolean = permissions
		? permissions.some(
				(perm) => perm.permissionName === "VIEW_FILE" && perm.enabled === true
			)
		: false

	// const fileHasNextUrlState = useSelector((state: RootState) => state.files.list.page.);
	const [sortCriteria, setSortCriteria] = useState<
		[{ field: string; order: string }]
	>([{ field: "correctPlick", order: "asc" }])
	const [filterName, setFilterName] = useState<undefined | string>(undefined)
	const [statuses, setStatuses] = useState<string[]>([])
	const [viewType, setViewType] = useState<undefined | string>("table")
	const [datePicker, setDatePicker] = useState<IDatePicker[]>([
		{
			startDate: null,
			endDate: null,
			key: "selection",
		},
	])

	const { ref, inView } = useInView({
		threshold: 1,
		triggerOnce: false,
	})

	const ButtonViewType = styled(Button)(({ theme }) => ({
		boxShadow:
			"0px 2px 4px -1px rgba(0,0,0,0.2),0px 4px 5px 0px rgba(0,0,0,0.14),0px 1px 10px 0px rgba(0,0,0,0.12)",
		"&:hover": {
			backgroundColor: theme.palette.secondary.contrastText,
			color: theme.palette.secondary.main,
			"& svg": {
				background: `${theme.palette.secondary.contrastText} !important`,
				color: `${theme.palette.secondary.main} !important`,
			},
		},
	}))

	const handleSearch = () => {
		const filters: { [key: string]: any } = setFilters()
		const plicksSort = sortCriteria
			.map((criteria) => `${criteria.field}:${criteria.order}`)
			.join(",")
		const bank = bankSelectedState?.id
		if (bank)
			dispatch(
				getFileDetail({
					bankId: bank,
					fileId: fileId,
					page: 0,
					pageSize: 10,
					filter: filters,
					sort: plicksSort,
				})
			)
				.then(() => {})
				.catch(() => {})
	}

	useEffect(() => {
		resetDefault()
	}, [])

	useEffect(() => {
		!hasViewTransactionEnabled && navigate("/")
	}, [hasViewTransactionEnabled])

	useEffect(() => {
		handleSearch()
	}, [authState?.isAuthenticate, sortCriteria, bankSelectedState?.id])

	const setFilters = useCallback(() => {
		const filter: { [key: string]: any } = {}

		if (filterName) filter.searchText = filterName
		if (bankSelectedState?.id) {
			filter.idBank = bankSelectedState.id
		}
		if (corporateSelectedState?.corporateSenderId) {
			filter.corporateSenderId = corporateSelectedState.corporateSenderId
		}
		if (datePicker?.[0]?.startDate || datePicker?.[0]?.endDate) {
			filter.rangeDate =
				format(datePicker?.[0]?.startDate, "yyyy-MM-dd") +
				":" +
				format(datePicker?.[0]?.endDate, "yyyy-MM-dd")
		}
		if (campaignSelectedState?.idLogo) {
			filter.campaign = campaignSelectedState.idLogo
		}

		return filter
	}, [
		bankSelectedState?.id,
		campaignSelectedState?.idLogo,
		corporateSelectedState?.corporateSenderId,
		filterName,
		datePicker?.[0]?.startDate,
		datePicker?.[0]?.endDate,
	])

	useEffect(() => {
		if (inView && plicksnextUrlState && !plickFetchLoadingState) {
			dispatch(fetchFileDetail({ url: plicksnextUrlState }))
				.then(() => {})
				.catch(() => {})
		}
	}, [authState?.isAuthenticate, inView, handleSearch])

	return (
		<>
			<Breadcrumb
				breadcrumbVoices={[
					{
						text: t("breadcrumbs.files", { ns: "plick" }),
						icon: <ListAltIcon fontSize="small" />,
						path: "/distinte",
					},
					{
						text: `${t("breadcrumbs.file", { ns: "plick" })} ${location?.state?.filename ?? fileName}`,
						icon: <ListAltIcon fontSize="small" />,
					},
				]}
			/>
			<Paper
				component="form"
				onSubmit={(event: FormEvent) => {
					event.stopPropagation()
					event.preventDefault()
					handleSearch()
				}}
				sx={{
					p: "2px 4px",
					display: "flex",
					alignItems: "center",
					justifyContent: "space-between",
					width: "100%",
				}}
			>
				<InputBase
					value={filterName}
					onChange={(event: ChangeEvent<HTMLTextAreaElement>) =>
						setFilterName(event.target.value)
					}
					sx={{ ml: 1, flex: 1 }}
					placeholder={t("search.placeholder", { ns: "plick" })}
					inputProps={{
						"aria-label": t("search.placeholder", { ns: "plick" }),
					}}
				/>
				<IconButton
					type="button"
					sx={{ p: "10px" }}
					aria-label={t("search.accessibility.label", { ns: "plick" })}
					onClick={() => handleSearch()}
				>
					<SearchIcon />
				</IconButton>
				<Divider sx={{ height: 28, m: 0.5 }} orientation="vertical" />
				<Grid container gap="16px" sx={{ width: "fit-content" }}>
					<FiltersPlick
						filterBank={filterBank}
						setFilterBank={setFilterBank}
						handleSearch={handleSearch}
						datePicker={datePicker}
						setDatePicker={setDatePicker}
						statuses={statuses}
						setStatuses={setStatuses}
					/>
					<Sort
						handleSearch={handleSearch}
						sortCriteria={sortCriteria}
						setSortCriteria={setSortCriteria}
						sortingOptions={[
							"id",
							"transactionId",
							"correctPlick",
							"activationDate",
							"note",
							"amount",
							"causal",
							"status",
							"receiverName",
						]}
						availableOptionsInitial={[
							"transactionId",
							"activationDate",
							"note",
							"amount",
							"causal",
							"status",
							"receiverName",
						]}
					/>
				</Grid>
			</Paper>

			<Grid
				container
				alignItems={"center"}
				justifyContent={"flex-end"}
				width={"100%"}
				sx={{ margin: "16px 0" }}
			>
				<Tooltip title={t("view.accessibility.grid", { ns: "plick" })}>
					<ButtonViewType
						variant={viewType === "grid" ? "contained" : "outlined"}
						aria-label={t("view.accessibility.grid", { ns: "plick" })}
						onClick={() => setViewType("grid")}
						sx={{
							backgroundColor:
								viewType === "grid"
									? theme.palette.secondary.main
									: theme.palette.primary.main,
						}}
						startIcon={
							<GridViewIcon
								style={{
									backgroundColor:
										viewType === "grid"
											? theme.palette.secondary.main
											: theme.palette.primary.main,
									color:
										viewType === "grid"
											? theme.palette.secondary.contrastText
											: theme.palette.primary.contrastText,
								}}
							/>
						}
					></ButtonViewType>
				</Tooltip>
				<Tooltip title={t("view.accessibility.table", { ns: "plick" })}>
					<ButtonViewType
						// variant={viewType === 'table' ? 'contained' : 'outlined'}
						aria-label={t("view.accessibility.table", { ns: "plick" })}
						onClick={(e) => setViewType("table")}
						sx={{
							marginLeft: "16px",
							backgroundColor:
								viewType === "table"
									? theme.palette.secondary.main
									: theme.palette.primary.main,
						}}
						startIcon={
							<TocIcon
								style={{
									backgroundColor:
										viewType === "table"
											? theme.palette.secondary.main
											: theme.palette.primary.main,
									color:
										viewType === "table"
											? theme.palette.secondary.contrastText
											: theme.palette.primary.contrastText,
								}}
							/>
						}
					></ButtonViewType>
				</Tooltip>
			</Grid>
			{PlickLoadingState ? (
				<SpinnerCard />
			) : (
				<>
					{viewType === "grid" ? (
						<PlickListComponent />
					) : (
						<EnhancedPlickTable
							sortCriteria={sortCriteria}
							setSortCriteria={setSortCriteria}
						/>
					)}
					{plickFetchLoadingState ? (
						<SpinnerCard />
					) : (
						<div ref={ref} style={{ height: "30px" }}></div>
					)}
				</>
			)}
		</>
	)
}

export default PlicksList
